import {
  activeWorkspaceIdAtom,
  selectedProcessIdAtom,
  timelineExpandedAtom,
} from '../../atoms/workspace';
import { type WorkspaceDoc } from '../../rxdb/collections/workspaces';
import {
  ActionIcon,
  Button,
  Group,
  Menu,
  Radio,
  Select,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useSetAtom } from 'jotai';
import { Clock, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ulid } from 'ulid';

export type WorkspaceProcessStats = {
  readonly activeProcessCount: number;
  readonly archivedProcessCount: number;
  readonly snoozedProcessCount: number;
  readonly totalProcessCount: number;
};

type DeleteWorkspaceInput = {
  readonly processDisposition: 'delete' | 'move';
  readonly targetWorkspaceId?: string;
  readonly workspaceId: string;
};

type WorkspaceActionsMenuProps = {
  readonly onDeleteWorkspace: (input: DeleteWorkspaceInput) => Promise<void>;
  readonly onRenameWorkspace: (id: string, title: string) => Promise<void>;
  readonly processStats: WorkspaceProcessStats;
  readonly showTimelineButton?: boolean;
  readonly workspace: WorkspaceDoc;
  readonly workspaces: WorkspaceDoc[];
};

type WorkspaceDeleteFormProps = {
  readonly modalId: string;
  readonly onDeleteWorkspace: (input: DeleteWorkspaceInput) => Promise<void>;
  readonly processStats: WorkspaceProcessStats;
  readonly workspace: WorkspaceDoc;
  readonly workspaces: WorkspaceDoc[];
};

type WorkspaceRenameFormProps = {
  readonly modalId: string;
  readonly onRenameWorkspace: (id: string, title: string) => Promise<void>;
  readonly workspace: WorkspaceDoc;
};

const WorkspaceRenameForm = ({
  modalId,
  onRenameWorkspace,
  workspace,
}: WorkspaceRenameFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState(workspace.title);

  const handleSubmit = async () => {
    const trimmed = title.trim();

    if (!trimmed || trimmed === workspace.title) {
      modals.close(modalId);
      return;
    }

    setIsSubmitting(true);

    try {
      await onRenameWorkspace(workspace.id, trimmed);
      modals.close(modalId);
      notifications.show({ message: 'Workspace renamed' });
    } catch (error) {
      notifications.show({
        color: 'red',
        message:
          error instanceof Error ? error.message : 'Failed to rename workspace',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <TextInput
        autoFocus
        label="Workspace name"
        onChange={(event) => setTitle(event.currentTarget.value)}
        value={title}
      />
      <Group
        justify="flex-end"
        mt="md"
      >
        <Button
          onClick={() => modals.close(modalId)}
          variant="default"
        >
          Cancel
        </Button>
        <Button
          loading={isSubmitting}
          type="submit"
        >
          Rename
        </Button>
      </Group>
    </form>
  );
};

const WorkspaceDeleteForm = ({
  modalId,
  onDeleteWorkspace,
  processStats,
  workspace,
  workspaces,
}: WorkspaceDeleteFormProps) => {
  const otherWorkspaces = useMemo(
    () => workspaces.filter((item) => item.id !== workspace.id),
    [workspace.id, workspaces],
  );
  const inbox = useMemo(
    () => otherWorkspaces.find((item) => item.position === 0),
    [otherWorkspaces],
  );
  const defaultTargetWorkspaceId = inbox?.id ?? otherWorkspaces[0]?.id ?? null;
  const hasProcesses = processStats.totalProcessCount > 0;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processDisposition, setProcessDisposition] = useState<
    'delete' | 'move'
  >('move');
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<null | string>(
    defaultTargetWorkspaceId,
  );

  const setActiveWorkspaceId = useSetAtom(activeWorkspaceIdAtom);
  const setSelectedProcessId = useSetAtom(selectedProcessIdAtom);

  const handleDelete = async () => {
    if (hasProcesses && processDisposition === 'move' && !targetWorkspaceId) {
      notifications.show({
        color: 'red',
        message: 'Choose a workspace to receive these processes',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await onDeleteWorkspace({
        processDisposition: hasProcesses ? processDisposition : 'delete',
        targetWorkspaceId:
          hasProcesses && processDisposition === 'move'
            ? (targetWorkspaceId ?? undefined)
            : undefined,
        workspaceId: workspace.id,
      });

      setSelectedProcessId(null);
      setActiveWorkspaceId(
        hasProcesses && processDisposition === 'move'
          ? (targetWorkspaceId ?? defaultTargetWorkspaceId)
          : defaultTargetWorkspaceId,
      );
      modals.close(modalId);
      notifications.show({ message: 'Workspace deleted' });
    } catch (error) {
      notifications.show({
        color: 'red',
        message:
          error instanceof Error ? error.message : 'Failed to delete workspace',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {hasProcesses ? (
        <>
          <Text
            c="dimmed"
            size="sm"
          >
            This workspace contains {processStats.totalProcessCount} processes:{' '}
            {processStats.activeProcessCount} active,{' '}
            {processStats.snoozedProcessCount} snoozed, and{' '}
            {processStats.archivedProcessCount} archived. Choose what should
            happen to them before deleting the workspace.
          </Text>

          <Radio.Group
            mt="md"
            onChange={(value) =>
              setProcessDisposition(value as 'delete' | 'move')
            }
            value={processDisposition}
          >
            <Radio
              label="Move processes to another workspace"
              value="move"
            />
            {processDisposition === 'move' && (
              <Select
                allowDeselect={false}
                aria-label="Target workspace"
                className="mt-2 ml-7"
                data={otherWorkspaces.map((item) => ({
                  label: item.title,
                  value: item.id,
                }))}
                onChange={setTargetWorkspaceId}
                value={targetWorkspaceId}
              />
            )}
            <Radio
              className="mt-3"
              color="red"
              label="Delete processes permanently"
              value="delete"
            />
          </Radio.Group>
        </>
      ) : (
        <Text
          c="dimmed"
          size="sm"
        >
          This workspace has no processes. Deleting it cannot be undone.
        </Text>
      )}

      <Group
        justify="flex-end"
        mt="lg"
      >
        <Button
          onClick={() => modals.close(modalId)}
          variant="default"
        >
          Cancel
        </Button>
        <Button
          color="red"
          loading={isSubmitting}
          onClick={handleDelete}
        >
          {hasProcesses && processDisposition === 'delete'
            ? 'Delete workspace and processes'
            : 'Delete workspace'}
        </Button>
      </Group>
    </div>
  );
};

export const WorkspaceActionsMenu = ({
  onDeleteWorkspace,
  onRenameWorkspace,
  processStats,
  showTimelineButton,
  workspace,
  workspaces,
}: WorkspaceActionsMenuProps) => {
  const setTimelineExpanded = useSetAtom(timelineExpandedAtom);
  const canDelete = workspace.position !== 0;
  const canRename = workspace.position !== 0;

  const openRenameDialog = () => {
    const modalId = ulid();
    modals.open({
      children: (
        <WorkspaceRenameForm
          modalId={modalId}
          onRenameWorkspace={onRenameWorkspace}
          workspace={workspace}
        />
      ),
      modalId,
      title: `Rename "${workspace.title}"`,
    });
  };

  const openDeleteDialog = () => {
    const modalId = ulid();
    modals.open({
      children: (
        <WorkspaceDeleteForm
          modalId={modalId}
          onDeleteWorkspace={onDeleteWorkspace}
          processStats={processStats}
          workspace={workspace}
          workspaces={workspaces}
        />
      ),
      modalId,
      title: `Delete "${workspace.title}"?`,
    });
  };

  return (
    <Menu
      position="bottom-end"
      shadow="md"
    >
      <Menu.Target>
        <Tooltip label="Workspace actions">
          <ActionIcon
            aria-label="Workspace actions"
            color="gray"
            size="sm"
            variant="subtle"
          >
            <MoreHorizontal size={16} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        {showTimelineButton && (
          <>
            <Menu.Item
              leftSection={<Clock size={14} />}
              onClick={() => setTimelineExpanded(true)}
            >
              Timeline
            </Menu.Item>
            <Menu.Divider />
          </>
        )}
        <Menu.Item
          disabled={!canRename}
          leftSection={<Pencil size={14} />}
          onClick={openRenameDialog}
        >
          Rename workspace
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          disabled={!canDelete}
          leftSection={<Trash2 size={14} />}
          onClick={openDeleteDialog}
        >
          Delete workspace
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
